import React, { useEffect, useState } from "react";
import FieldModelPanel from "@/components/FieldModelPanel";
import PreflightPanel from "@/components/PreflightPanel";
import CompetitorPoolGrid from "@/components/CompetitorPoolGrid";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users, TrendUp, ShieldCheck, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Admin console — internal support UI (Sprint 60.46).
 *
 * Access is gated by `user.is_admin`. Any authenticated user without
 * that flag sees a 403 message. Every backend endpoint additionally
 * enforces `require_admin`, so a UI tamper can never actually cause
 * damage — the frontend is a convenience layer over the guarded API.
 */
export default function Admin() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const refreshMetrics = async () => {
    try {
      const r = await http.get("/admin/metrics");
      setMetrics(r.data);
    } catch (e) {
      if (e?.response?.status === 403) return;
      toast.error("Failed to load metrics");
    }
  };
  const searchUsers = async (q = query) => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/users?query=${encodeURIComponent(q)}&limit=50`);
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    refreshMetrics();
    searchUsers("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white p-8" data-testid="admin-not-authorized">
        <div className="max-w-lg mx-auto border border-[#FF3B30] bg-[#1a0808] p-6">
          <div className="heading font-black uppercase text-lg mb-2">403 · Admin Only</div>
          <div className="text-sm text-zinc-400">
            This console is restricted to WarPaint DFS support staff. If you believe you should have
            access, contact <a href="mailto:support@warpaintdfs.com" className="text-[#39FF14] underline">support@warpaintdfs.com</a>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6" data-testid="admin-console">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Internal</div>
            <h1 className="heading text-3xl font-black uppercase">Admin Console</h1>
          </div>
          <button
            onClick={() => { refreshMetrics(); searchUsers(); }}
            className="border border-zinc-800 hover:border-[#39FF14] px-3 py-2 text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] flex items-center gap-2"
            data-testid="admin-refresh"
          >
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </header>

        {/* Metrics tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-metrics">
          <MetricTile label="Total Users" value={metrics?.total_users} icon={<Users size={18} weight="fill" />} color="text-white" />
          <MetricTile label="Email Verified" value={metrics?.email_verified} icon={<ShieldCheck size={18} weight="fill" />} color="text-[#39FF14]" />
          <MetricTile label="Active Subs" value={metrics?.active_subscriptions} icon={<Sparkle size={18} weight="fill" />} color="text-[#39FF14]" />
          <MetricTile label="MRR (USD)" value={metrics ? `$${metrics.mrr_usd}` : null} icon={<TrendUp size={18} weight="fill" />} color="text-[#FFCC00]" />
        </div>

        {/* Sprint 60.66 — Ownership Override tab.  Blends admin-uploaded
            ownership projections 50/50 with WarPaint's P1 native model. */}
        <OwnershipOverridePanel />

        {/* Sprint 60.98 — Projection Override tab. Same 50/50 blend
            pattern, but for fantasy-point projections. Blended BEFORE
            ownership is computed so the ownership model sees the
            corrected projection. */}
        <ProjectionOverridePanel />

        <SgoSnapshotPanel />

        <PreflightPanel />
        <FieldModelPanel />
        <CompetitorPoolGrid />
        <OwnershipActualsPanel />
        <CalibrationPanel />

        {/* Search */}
        <div className="bg-[#121212] border border-zinc-800 p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Search users</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchUsers()}
              placeholder="email or user_id"
              data-testid="admin-user-search"
              className="flex-1 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-sm"
            />
            <button
              onClick={() => searchUsers()}
              disabled={busy}
              data-testid="admin-user-search-btn"
              className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] px-4"
            >
              Search
            </button>
          </div>
        </div>

        {/* User table */}
        <div className="bg-[#121212] border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-user-table">
            <thead>
              <tr className="text-[10px] mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Plan</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Verified</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-4 py-2 text-zinc-300 mono text-xs">{u.email}</td>
                  <td className="px-4 py-2 text-zinc-400">{u.plan || "—"}</td>
                  <td className={`px-4 py-2 ${u.subscription_status === "active" ? "text-[#39FF14]" : "text-zinc-500"}`}>{u.subscription_status || "inactive"}</td>
                  <td className={`px-4 py-2 ${u.email_verified ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>{u.email_verified ? "yes" : "no"}</td>
                  <td className="px-4 py-2 text-zinc-500 mono text-[10px]">{u.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setSelectedUser(u)}
                      data-testid={`admin-manage-${u.user_id}`}
                      className="text-[10px] mono uppercase tracking-widest text-[#39FF14] hover:text-white"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500 text-sm">No users match this query</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedUser && (
        <UserManageModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdated={() => { searchUsers(); refreshMetrics(); }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sprint 60.66 — Ownership Override Panel
// ---------------------------------------------------------------------------
// CSV upload for external ownership projections.
// Backend blends 50/50 with the P1 native model app-wide until cleared or
// auto-expired at 7 days. Regular users never see the panel or its output
// as flagged data — from their POV the ownership numbers just get sharper.
function OwnershipOverridePanel() {
  const [status, setStatus] = useState(null);
  const [source, setSource] = useState("external");
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await http.get("/admin/ownership-override");
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("Failed to load override status");
      }
    }
  };
  useEffect(() => { refresh(); }, []);

  const parseCsv = (raw) => {
    // Robust minimal parser — supports:
    //   dk_id,ownership
    //   name,team,ownership
    //   dk_id,name,team,ownership_pct
    // Auto-detects columns from a header row.
    const lines = raw.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].toLowerCase().split(",").map((s) => s.trim());
    const idx = {
      dk_id: header.findIndex((h) => h === "dk_id" || h === "id" || h === "playerid"),
      name: header.findIndex((h) => h === "name" || h === "player" || h === "player_name"),
      team: header.findIndex((h) => h === "team" || h === "teamabbrev"),
      own: header.findIndex((h) => h === "ownership_pct" || h === "ownership" || h === "own" || h === "projown"),
    };
    if (idx.own < 0) {
      toast.error("CSV must have an 'ownership' or 'ownership_pct' column");
      return [];
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((s) => s.trim().replace(/^"|"$/g, ""));
      const ownStr = cols[idx.own];
      const ownership_pct = parseFloat(ownStr);
      if (isNaN(ownership_pct)) continue;
      rows.push({
        dk_id: idx.dk_id >= 0 ? cols[idx.dk_id] : undefined,
        name: idx.name >= 0 ? cols[idx.name] : undefined,
        team: idx.team >= 0 ? cols[idx.team] : undefined,
        ownership_pct,
      });
    }
    return rows;
  };

  const upload = async () => {
    const rows = parseCsv(csv);
    if (!rows.length) {
      toast.error("No valid rows parsed from CSV");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/admin/ownership-override", {
        rows,
        source,
        slate_type: "MAIN",
      });
      toast.success(
        `Uploaded · ${r.data.resolved} resolved · ${r.data.unmatched_total} unmatched`,
      );
      setCsv("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear the active ownership override? P1 model will run pure.")) return;
    setBusy(true);
    try {
      await http.delete("/admin/ownership-override");
      toast.success("Override cleared");
      refresh();
    } catch (e) {
      toast.error("Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-ownership-override">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
            Sprint 60.66 · Native P1 Blend
          </div>
          <div className="heading text-lg font-black uppercase">Ownership Override</div>
        </div>
        {status?.active && (
          <div className="mono text-[10px] text-[#39FF14] border border-[#39FF14] px-2 py-1 whitespace-nowrap">
            ACTIVE · {status.overrides_count} players · src: {status.source}
          </div>
        )}
      </div>
      <p className="text-[11px] mono text-zinc-500 mb-3 leading-relaxed">
        Paste a CSV from any external ownership / projection tool.
        Backend blends 50/50 with the P1 native model app-wide.
        Auto-expires after 7 days so a stale upload can&apos;t poison next week&apos;s slate.
        Accepts <code>dk_id,ownership_pct</code> or <code>name,team,ownership_pct</code>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <input
          type="text"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="source label (e.g. external)"
          data-testid="admin-override-source"
          className="bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-sm md:col-span-1"
        />
        <button
          onClick={upload}
          disabled={busy || !csv.trim()}
          data-testid="admin-override-upload-btn"
          className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] px-4 py-2"
        >
          {busy ? "Uploading…" : "Upload Override"}
        </button>
        <button
          onClick={clear}
          disabled={busy || !status?.active}
          data-testid="admin-override-clear-btn"
          className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black disabled:opacity-30 font-black uppercase tracking-widest text-[11px] px-4 py-2 transition-colors"
        >
          Clear Active
        </button>
        <button
          onClick={refresh}
          disabled={busy}
          data-testid="admin-override-refresh-btn"
          className="border border-zinc-800 hover:border-zinc-600 text-zinc-500 font-black uppercase tracking-widest text-[11px] px-4 py-2 transition-colors"
        >
          Refresh Status
        </button>
      </div>
      <textarea
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder={"dk_id,name,team,ownership_pct\n39971423,Josh Allen,BUF,28.5\n39971457,Christian McCaffrey,SF,34.2"}
        rows={8}
        data-testid="admin-override-csv"
        className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-xs mono font-mono"
      />
      {status?.active && (
        <div className="mt-3 text-[10px] mono text-zinc-500">
          Uploaded {status.active_at?.slice(0, 19)}Z by {status.uploaded_by_email} ·
          {" "}Sample: {Object.entries(status.overrides_sample || {}).slice(0, 3).map(
            ([id, own]) => `${id}=${own}%`,
          ).join(" · ")}
        </div>
      )}
    </div>
  );
}



// Sprint 61.4 — DK contest standings CSV → actual %Drafted → model accuracy + auto-calibration.
function OwnershipActualsPanel() {
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [result, setResult] = useState(null);
  const [cal, setCal] = useState(null);

  useEffect(() => {
    http.get("/admin/ownership/accuracy").then((r) => setCal(r.data)).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const csv_text = await file.text();
      const r = await http.post("/admin/ownership/actuals", { csv_text, label: label || file.name });
      setResult(r.data);
      toast.success(`Calibrated on ${r.data.parsed_rows} players · overall MAE ${r.data.report?.overall?.mae ?? "—"}pp`);
      const c = await http.get("/admin/ownership/accuracy");
      setCal(c.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const byPos = result?.report?.by_pos || cal?.calibration?.last_report?.by_pos || null;
  const temps = cal?.calibration?.temp_by_pos || {};

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-ownership-actuals">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Ownership model · calibration</div>
          <div className="heading text-lg font-black uppercase">Post-Slate Grading (DK Standings CSV)</div>
          <div className="text-xs text-zinc-400 mt-1 max-w-2xl">
            After a slate, open the contest results on DraftKings → <span className="text-white">Export Lineups</span>. That CSV has
            a <span className="mono">%Drafted</span> column for every player in the contest. Upload it here: we score our model
            against it (MAE / bias per position), grade our <span className="text-white">projections vs FPTS</span> (by position and
            by source), score your own portfolio, auto-tune the ownership temperature, and publish the scoreboard every user sees on My Lineups.
            {cal?.calibration?.n_slates ? ` Calibrated on ${cal.calibration.n_slates} slate(s).` : " Not calibrated yet."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label · e.g. W1 Milly Maker"
            data-testid="admin-own-actuals-label"
            className="bg-black border border-zinc-800 px-2 py-2 text-xs w-48 focus:border-[#39FF14] outline-none"
          />
          <label
            data-testid="admin-own-actuals-upload"
            className={`px-3 py-2 text-[11px] uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
          >
            {busy ? "Scoring…" : "Upload standings CSV"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="admin-own-actuals-input" />
          </label>
        </div>
      </div>
      {byPos && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4" data-testid="admin-own-accuracy-grid">
          {["QB", "RB", "WR", "TE", "DST"].map((p) => {
            const r = byPos[p];
            return (
              <div key={p} className="border border-zinc-800 p-2">
                <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">{p} · n={r?.n ?? 0}</div>
                <div className="heading font-black text-white mt-1">MAE {r ? `${r.mae}pp` : "—"}</div>
                <div className="mono text-[10px] text-zinc-400">bias {r ? `${r.bias > 0 ? "+" : ""}${r.bias}` : "—"} · corr {r?.corr ?? "—"}</div>
                <div className="mono text-[10px] text-zinc-500">τ {temps[p] ?? "1.0"} · top10 {r ? `${r.top10_mae}pp` : "—"}</div>
              </div>
            );
          })}
        </div>
      )}
      {result?.projection_report?.available && (
        <div className="mt-3 border border-[#39FF14]/30 bg-[#39FF14]/5 p-3 text-[11px] mono" data-testid="admin-projection-report">
          <div className="text-[#39FF14] font-bold uppercase tracking-widest text-[10px] mb-1">Projection accuracy · published to My Lineups</div>
          MAE {result.projection_report.overall?.mae} pts · bias {result.projection_report.overall?.bias} · corr {result.projection_report.overall?.corr}
          {" · "}market {result.projection_report.by_source?.market?.mae ?? "—"} vs fallback {result.projection_report.by_source?.fallback?.mae ?? "—"}
          {result.portfolio?.available && <> · your best lineup {result.portfolio.best?.actual} pts (#{result.portfolio.best?.lineup_id})</>}
          <div className="text-zinc-400 mt-1">
            {["QB", "RB", "WR", "TE", "DST"].map((p) => `${p} ${result.projection_report.by_pos?.[p]?.mae ?? "—"}`).join(" · ")}
          </div>
        </div>
      )}
      {result?.report?.worst_misses?.length > 0 && (
        <div className="mt-3 mono text-[10px] text-zinc-400" data-testid="admin-own-worst-misses">
          Worst misses: {result.report.worst_misses.slice(0, 6).map((m) => `${m.name} ${m.modeled}→${m.actual}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

// SGO data snapshot — download the live CACHE (projections + Vegas +
// injuries) as JSON on production, upload it on preview. Zero SGO wire hits.
function SgoSnapshotPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    setBusy(true);
    try {
      const r = await http.get("/admin/sgo/export");
      const snap = r.data || {};
      const blob = new Blob([JSON.stringify(snap)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `sgo_snapshot_${(snap.captured_at || "now").slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Snapshot saved · ${Object.keys(snap.sgo_projections_by_sgo_id || {}).length} projections · ${(snap.vegas || []).length} Vegas games`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const snap = JSON.parse(await file.text());
      const r = await http.post("/admin/sgo/import", snap);
      setResult(r.data);
      toast.success(`Imported · ${r.data.projections_loaded} projections · ${r.data.vegas_games_loaded} Vegas games`);
      window.dispatchEvent(new CustomEvent("wp:pool-refreshed", { detail: { source: "sgo-import" } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Import failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const pullNow = async () => {
    setBusy(true);
    try {
      const r = await http.post("/admin/sgo/refresh-now");
      const d = r.data || {};
      if (d.projections_error || d.vegas_error) {
        toast.error(d.projections_error || d.vegas_error);
      } else {
        toast.success(`SGO pulled · ${d.projections ?? 0} projections · ${d.vegas ?? 0} Vegas games · month ${d.month_entities?.used ?? "?"}/${d.month_entities?.cap ?? "?"} events`);
      }
      setResult({ projections_loaded: d.projections, vegas_games_loaded: d.vegas, injuries_loaded: "—", captured_at: d.sgo_last_refresh });
      try { await http.post("/pool/refresh-projections"); } catch (_) { /* pool may not be loaded */ }
      window.dispatchEvent(new CustomEvent("wp:pool-refreshed", { detail: { source: "sgo-refresh-now" } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SGO pull failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-sgo-snapshot">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Data snapshot · SGO + Vegas + injuries</div>
          <div className="heading text-lg font-black uppercase">SGO Snapshot</div>
          <div className="text-xs text-zinc-400 mt-1 max-w-2xl">
            <span className="text-white">Pull SGO now</span> forces a live projections + Vegas refresh (~20 billed events;
            the scheduler otherwise runs 6am CT / Thu / Sun). Download the cache as JSON, or upload one captured elsewhere —
            import never touches the SGO wire.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="admin-sgo-pull-now-btn"
            onClick={pullNow}
            disabled={busy}
            className="px-3 py-2 text-[11px] uppercase tracking-widest bg-[#39FF14] text-black font-black hover:bg-[#2ee30d] transition-colors disabled:opacity-50"
          >
            {busy ? "Working…" : "Pull SGO now"}
          </button>
          <button
            type="button"
            data-testid="admin-sgo-export-btn"
            onClick={download}
            disabled={busy}
            className="px-3 py-2 text-[11px] uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors disabled:opacity-50"
          >
            Download snapshot
          </button>
          <label
            data-testid="admin-sgo-import-label"
            className={`px-3 py-2 text-[11px] uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
          >
            Upload snapshot
            <input type="file" accept="application/json,.json" className="hidden" onChange={upload} data-testid="admin-sgo-import-input" />
          </label>
        </div>
      </div>
      {result && (
        <div className="mono text-[11px] text-zinc-400 mt-3" data-testid="admin-sgo-import-result">
          Loaded {result.projections_loaded} projections · {result.vegas_games_loaded} Vegas games · {result.injuries_loaded} injuries · captured {result.captured_at}
        </div>
      )}
    </div>
  );
}


function MetricTile({ label, value, icon, color }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={color}>{icon}</span>
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
      <div className={`text-2xl font-black ${color}`}>{value ?? "—"}</div>
    </div>
  );
}

function UserManageModal({ user, onClose, onUpdated }) {
  const [plan, setPlan] = useState(user.plan || "");
  const [status, setStatus] = useState(user.subscription_status || "inactive");
  const [daysExtension, setDaysExtension] = useState(0);
  const [reason, setReason] = useState("");
  const [impReason, setImpReason] = useState("");
  const [busy, setBusy] = useState(false);

  const applyOverride = async () => {
    if (reason.trim().length < 5) { toast.error("Reason ≥5 chars"); return; }
    setBusy(true);
    try {
      const payload = { reason: reason.trim() };
      if (plan !== user.plan) payload.plan = plan || null;
      if (status !== user.subscription_status) payload.subscription_status = status;
      if (Number(daysExtension) > 0) payload.days_extension = Number(daysExtension);
      await http.post(`/admin/users/${user.user_id}/subscription`, payload);
      toast.success("Override applied + audit-logged");
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally { setBusy(false); }
  };

  const verifyEmail = async () => {
    setBusy(true);
    try {
      await http.post(`/admin/users/${user.user_id}/verify-email`);
      toast.success("Email marked verified");
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const impersonate = async () => {
    if (impReason.trim().length < 5) { toast.error("Reason ≥5 chars"); return; }
    setBusy(true);
    try {
      const r = await http.post(`/admin/users/${user.user_id}/impersonate`, {
        reason: impReason.trim(), ttl_minutes: 15,
      });
      // Store impersonation token so /auth/me returns the target user's context.
      localStorage.setItem("wp_impersonation", JSON.stringify({
        token: r.data.impersonation_token,
        target_email: r.data.target_email,
        expires_at: Date.now() + (r.data.expires_in_minutes * 60 * 1000),
      }));
      toast.warning(`Impersonating ${r.data.target_email} for ${r.data.expires_in_minutes}min`, {
        description: "Refresh the app to load the target user's view.",
        duration: 10000,
      });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-w-lg w-full bg-[#0a0a0a] border-2 border-zinc-800 max-h-[90vh] overflow-y-auto">
        <div className="border-b border-zinc-900 px-5 py-4">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Manage</div>
          <div className="heading font-black uppercase text-lg">{user.email}</div>
          <div className="text-[10px] mono text-zinc-500 mt-1">{user.user_id}</div>
        </div>
        <div className="p-5 space-y-6">
          {/* Subscription override */}
          <section>
            <div className="heading font-bold text-white mb-3">Subscription override</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="block">
                <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">Plan</div>
                <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-2 text-sm">
                  <option value="">— none —</option>
                  <option value="weekly">weekly</option>
                  <option value="monthly">monthly (legacy)</option>
                  <option value="season">season (legacy)</option>
                  <option value="beta">beta</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">Status</div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-2 text-sm">
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="past_due">past_due</option>
                </select>
              </label>
            </div>
            <label className="block mb-2">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">Extend expiry (days)</div>
              <input type="number" min="0" value={daysExtension} onChange={(e) => setDaysExtension(e.target.value)} className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-2 text-sm" data-testid="admin-days-extension" />
            </label>
            <label className="block mb-2">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">Reason (audit log)</div>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Refund for Sunday outage / beta grant / etc." className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-2 text-sm" data-testid="admin-override-reason" />
            </label>
            <button onClick={applyOverride} disabled={busy || reason.trim().length < 5} data-testid="admin-apply-override" className="w-full bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] py-2.5">
              Apply override
            </button>
          </section>

          {/* Email verify */}
          <section className="border-t border-zinc-900 pt-4">
            <div className="heading font-bold text-white mb-2">Email verification</div>
            <div className="text-xs text-zinc-500 mb-2">Manually mark verified if user says the email never arrived and they can't receive one.</div>
            <div className="flex items-center gap-3">
              <div className={`text-[10px] mono ${user.email_verified ? "text-[#39FF14]" : "text-[#FFCC00]"}`}>
                {user.email_verified ? "Already verified" : "NOT verified"}
              </div>
              <button onClick={verifyEmail} disabled={busy || user.email_verified} data-testid="admin-verify-email" className="ml-auto bg-transparent border border-zinc-800 hover:border-[#39FF14] hover:text-[#39FF14] text-zinc-400 text-[10px] mono uppercase tracking-widest px-3 py-2 disabled:opacity-40">
                Mark verified
              </button>
            </div>
          </section>

          {/* Impersonate */}
          <section className="border-t border-zinc-900 pt-4">
            <div className="heading font-bold text-white mb-2">Impersonate (15-min JWT)</div>
            <div className="text-xs text-zinc-500 mb-2">Reproduces a bug from this user's POV. Every impersonation is audit-logged + fired to Sentry.</div>
            <input type="text" value={impReason} onChange={(e) => setImpReason(e.target.value)} placeholder="Reason (audit log)" className="w-full bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-2 text-sm mb-2" data-testid="admin-impersonate-reason" />
            <button onClick={impersonate} disabled={busy || impReason.trim().length < 5} data-testid="admin-impersonate-btn" className="w-full bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] py-2.5">
              Impersonate this user
            </button>
          </section>
        </div>
        <div className="border-t border-zinc-900 px-5 py-3 text-right">
          <button onClick={onClose} className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="admin-manage-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sprint 60.98 — Projection Override Panel
// ---------------------------------------------------------------------------
// Mirror of OwnershipOverridePanel, but drives fantasy-point projections.
// Backend blends 50/50 with the SGO-derived `our_proj` in
// `_rebuild_derived`; blended projection then feeds the ownership model
// so the override cascades through both signals. All positions accepted
// (including DST). 7-day auto-expire; TTL kicks in on backend read.
function ProjectionOverridePanel() {
  const [status, setStatus] = useState(null);
  const [source, setSource] = useState("admin_manual");
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await http.get("/admin/projection-override");
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("Failed to load projection override status");
      }
    }
  };
  useEffect(() => { refresh(); }, []);

  const parseCsv = (raw) => {
    const lines = raw.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].toLowerCase().split(",").map((s) => s.trim());
    const idx = {
      dk_id: header.findIndex((h) => h === "dk_id" || h === "id" || h === "playerid"),
      name: header.findIndex((h) => h === "name" || h === "player" || h === "player_name"),
      team: header.findIndex((h) => h === "team" || h === "teamabbrev"),
      proj: header.findIndex((h) =>
        h === "projection" || h === "proj" || h === "projected_points"
        || h === "fpts" || h === "points",
      ),
    };
    if (idx.proj < 0) {
      toast.error("CSV must have a 'projection' (or proj / fpts / points) column");
      return [];
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((s) => s.trim().replace(/^"|"$/g, ""));
      const projStr = cols[idx.proj];
      const projection = parseFloat(projStr);
      if (isNaN(projection)) continue;
      rows.push({
        dk_id: idx.dk_id >= 0 ? cols[idx.dk_id] : undefined,
        name: idx.name >= 0 ? cols[idx.name] : undefined,
        team: idx.team >= 0 ? cols[idx.team] : undefined,
        projection,
      });
    }
    return rows;
  };

  const upload = async () => {
    const rows = parseCsv(csv);
    if (!rows.length) {
      toast.error("No valid rows parsed from CSV");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/admin/projection-override", {
        rows,
        source,
        slate_type: "MAIN",
      });
      toast.success(
        `Uploaded · ${r.data.resolved} resolved · ${r.data.unmatched_total} unmatched`,
      );
      setCsv("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear the active projection override? Native model will run pure.")) return;
    setBusy(true);
    try {
      await http.delete("/admin/projection-override");
      toast.success("Projection override cleared");
      refresh();
    } catch (e) {
      toast.error("Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-projection-override">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
            Sprint 60.98 · Native SGO Blend
          </div>
          <div className="heading text-lg font-black uppercase">Projection Override</div>
        </div>
        {status?.active && (
          <div className="mono text-[10px] text-[#39FF14] border border-[#39FF14] px-2 py-1 whitespace-nowrap">
            ACTIVE · {status.overrides_count} players · src: {status.source}
          </div>
        )}
      </div>
      <p className="text-[11px] mono text-zinc-500 mb-3 leading-relaxed">
        Paste a CSV to override player fantasy projections app-wide.
        Blended 50/50 with the SGO-derived model; blended projection
        also feeds the ownership model so both signals stay coherent.
        Accepts all positions (QB / RB / WR / TE / DST). Auto-expires after 7 days.
        Columns: <code>dk_id,projection</code> or <code>name,team,projection</code>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <input
          type="text"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="source label"
          data-testid="admin-proj-override-source"
          className="bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-sm md:col-span-1"
        />
        <button
          onClick={upload}
          disabled={busy || !csv.trim()}
          data-testid="admin-proj-override-upload-btn"
          className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[11px] px-4 py-2"
        >
          {busy ? "Uploading…" : "Upload Projections"}
        </button>
        <button
          onClick={clear}
          disabled={busy || !status?.active}
          data-testid="admin-proj-override-clear-btn"
          className="border border-[#FF3B30]/40 hover:bg-[#FF3B30] hover:text-black disabled:opacity-40 text-[#FF3B30] font-black uppercase tracking-widest text-[11px] px-4 py-2"
        >
          Clear Override
        </button>
      </div>
      <textarea
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        rows={8}
        data-testid="admin-proj-override-csv"
        placeholder="dk_id,projection&#10;12345,21.4&#10;67890,15.8"
        className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-xs mono"
      />
    </div>
  );
}


// Sprint 62.5 — Calibration database: pre-lock snapshots → graded weeks →
// learned ownership temperatures + variance scales.
function CalibrationPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => http.get("/admin/calibration/status").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const snapshot = async () => {
    setBusy(true);
    try {
      const r = await http.post("/admin/calibration/snapshot");
      toast.success(`Snapshot saved · ${r.data.snapshots} slate${r.data.snapshots === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Snapshot failed"); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset learned calibration (ownership temperatures + variance scales) to factory defaults? Snapshots and graded weeks are kept.")) return;
    try { await http.post("/admin/calibration/reset"); toast.success("Calibration reset"); load(); }
    catch (e) { toast.error("Reset failed"); }
  };
  const cal = data?.calibration || {};
  const mode = data?.mode || { mode: "cold" };
  const bs = cal.bootstrap || {};
  const fitBootstrap = async () => {
    setBusy(true);
    try {
      const r = await http.post("/admin/calibration/bootstrap");
      const b = r.data.bootstrap || {};
      toast.success(`Bootstrap fitted from ${b.n_sources} sources · ${b.n_players} players · own MAE ${b.own_mae_before} → ${b.own_mae_after}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bootstrap failed"); } finally { setBusy(false); }
  };
  const clearBootstrap = async () => {
    if (!window.confirm("Clear the preseason bootstrap and return to factory defaults?")) return;
    try { await http.delete("/admin/calibration/bootstrap"); toast.success("Bootstrap cleared"); load(); }
    catch (e) { toast.error("Clear failed"); }
  };
  const modeStyle = { results: "text-[#39FF14] border-[#39FF14]/50", bootstrap: "text-[#FFCC00] border-[#FFCC00]/50", cold: "text-[#FF3B30] border-[#FF3B30]/50" }[mode.mode] || "text-zinc-400 border-zinc-700";
  const fmt = (o) => (o && Object.keys(o).length ? Object.entries(o).map(([k, v]) => `${k} ${v}`).join(" · ") : "factory defaults");
  return (
    <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="admin-calibration-panel">
      <div className={`border ${modeStyle} bg-black/30 px-3 py-2.5 mb-3 flex items-center justify-between gap-3 flex-wrap`} data-testid="calibration-mode-strip">
        <div className="min-w-0">
          <div className={`mono text-[10px] uppercase tracking-widest font-bold ${modeStyle.split(" ")[0]}`} data-testid="calibration-mode">
            Calibration mode · {mode.mode === "results" ? "Real results" : mode.mode === "bootstrap" ? "Preseason bootstrap" : "Cold start"}
          </div>
          <div className="text-[11px] text-zinc-300 mt-0.5" data-testid="calibration-mode-detail">{mode.detail}</div>
          <div className="text-[10px] text-zinc-500 mt-1 max-w-3xl leading-relaxed">
            Bootstrap learns a handful of global dials (ownership temperature and projection scale per position) from the consensus
            you loaded in Field Model — no raw numbers leave your account — so every user starts Week 1 calibrated. The first DK
            standings you grade retires it automatically and real results own the dials from then on (temps blend 50/50 on handoff).
          </div>
          {bs.fitted_at && (
            <div className="mono text-[9px] text-zinc-500 mt-1" data-testid="calibration-bootstrap-fit">
              fitted {String(bs.fitted_at).slice(0, 16).replace("T", " ")} · own MAE {bs.own_mae_before} → {bs.own_mae_after} · proj scale {fmt(bs.proj_scale_by_pos)}
              {bs.active === false && <span className="text-[#39FF14]"> · retired by {bs.retired_by}</span>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={fitBootstrap} disabled={busy || mode.mode === "results"} data-testid="calibration-bootstrap-fit-btn"
            title={mode.mode === "results" ? "Real weeks are graded — results own the calibration" : "Fit from the consensus loaded in Field Model"}
            className="bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-40 text-black font-black uppercase tracking-widest text-[10px] px-3 py-2">
            {busy ? "Fitting…" : bs.active ? "Re-fit bootstrap" : "Fit preseason bootstrap"}
          </button>
          {bs.active && (
            <button onClick={clearBootstrap} data-testid="calibration-bootstrap-clear-btn"
              className="border border-zinc-700 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30] uppercase tracking-widest text-[10px] px-3 py-2">
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="heading text-lg font-black uppercase">Calibration Database</div>
          <div className="text-[11px] text-zinc-400 mt-1 max-w-3xl leading-relaxed">
            Pre-lock snapshots are taken automatically at <span className="text-white">12:55 PM ET Sunday</span> and
            <span className="text-white"> 8:15 PM ET Thu/Mon</span>. Upload the DK standings CSV above on Monday — the week is graded
            against the snapshot (never the post-game pool) and the errors feed the ownership softmax temperature and the
            per-position variance scale used by the simulator and ceiling column.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={snapshot} disabled={busy} data-testid="admin-calibration-snapshot"
            className="bg-[#39FF14] hover:bg-[#32e012] disabled:opacity-50 text-black font-black uppercase tracking-widest text-[10px] px-3 py-2">
            {busy ? "Saving…" : "Snapshot now"}
          </button>
          <button onClick={reset} data-testid="admin-calibration-reset"
            className="border border-zinc-700 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30] uppercase tracking-widest text-[10px] px-3 py-2">
            Reset
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-[11px] mono">
        <div className="border border-zinc-900 p-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Weeks graded</div>
          <div className="text-[#39FF14] text-lg font-black" data-testid="admin-calibration-weeks">{cal.n_weeks || 0}</div>
          <div className="text-zinc-500">{(data?.snapshots || []).length} snapshots · {(data?.snapshots || []).filter((s) => !s.graded).length} awaiting standings</div>
        </div>
        <div className="border border-zinc-900 p-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Ownership temperature (τ × by pos)</div>
          <div className="text-zinc-200 mt-1" data-testid="admin-calibration-temps">{fmt(cal.temp_by_pos)}</div>
        </div>
        <div className="border border-zinc-900 p-2">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Variance scale (σ × by pos)</div>
          <div className="text-zinc-200 mt-1" data-testid="admin-calibration-cv">{fmt(cal.cv_scale_by_pos)}</div>
        </div>
      </div>
      {(data?.weeks || []).length > 0 && (
        <div className="mt-3 text-[10px] mono text-zinc-400 space-y-1" data-testid="admin-calibration-history">
          {data.weeks.slice(0, 6).map((w) => (
            <div key={w._id} className="flex flex-wrap gap-x-3 border-t border-zinc-900 pt-1">
              <span className="text-white">{w.label || w._id}</span>
              <span>proj MAE {w.report?.projection?.mae ?? "—"}{w.report?.projection?.market_mae != null ? ` (market ${w.report.projection.market_mae})` : ""}</span>
              <span>own MAE {w.report?.ownership?.mae ?? "—"}{w.report?.ownership?.market_mae != null ? ` (market ${w.report.ownership.market_mae})` : ""}</span>
              <span>matched {w.report?.n_matched}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
